import emitter from "@webcore/core/events/eventemitter.ts";
import * as Sentry from "@sentry/react";
import { initializeCreatorData } from "@/data/data";
import { captureArtworkFromUrl } from "@/data/artworks";
import { getRelativePath, patchWorkerBasePath } from "@/utils/basePath";
import { renderFatalBootError } from "@/utils/bootError";
import { isLiveSite } from "@/utils/liveSite";
import { applySearchIndexing } from "@/utils/searchIndexing";

// Make web-core's origin-root worker URLs (e.g. /js/qrcodeWorker.js) resolve
// under the deploy base path. Runs before main.app boots the 8thwall/QR
// pipeline that constructs the worker.
patchWorkerBasePath();

// Only the live home page is indexable. Reads the path the visitor arrived on,
// so it runs before the sign is moved out of the URL below.
applySearchIndexing();

// Move the scanned sign out of the URL and into this tab's memory, so the
// address bar reads as the site root from the first paint. Must run before the
// UI renders — the router decides the root route from the captured sign.
captureArtworkFromUrl();

if (import.meta.env.DEV ) {
  // import("./utils/stats").then(({ initStats }) => initStats()).catch(() => {});
  import("./utils/eruda").then(({ initEruda }) => initEruda()).catch(() => {});
}

// A content failure is fatal: the artwork cache in src/collection/ is what the
// whole experience is built from. Show it instead of booting a UI that looks
// fine and has no AR behind it.
initializeCreatorData().then(
  () => {
    import("./main.app.ui").then(({ initUI }) => {
      initUI();
    });
  },
  (error) => {
    renderFatalBootError(error);
  },
);

emitter.on("ui-ready", () => {
  // `/dev/*` routes are self-contained dev scenes with no 8thwall runtime.
  // Booting main.app there installs a real `VideoRecorderController` whose
  // `record-start` listener fires AFTER the dev mock's (production is dynamic
  // -imported here, the mock is registered when a dev scene mounts). With no
  // `mediaRecorder` ever set, the production controller emits
  // `record-started: { success: false }` which overrides the mock's success
  // and bounces `useRecording` straight back to idle. Skip the app boot
  // entirely on dev routes so the mock owns the recording event bus.
  if (
    typeof window !== "undefined" &&
    getRelativePath().startsWith("/dev")
  ) {
    return;
  }
  import("./main.app").then(async ({ initApp }) => {
    try {
      await initApp();
    } finally {
      // Signal that the 8thwall app instance has booted enough that clicking
      // launch will actually do something. The splash listens for this and
      // swaps a spinner for the launch button until it fires.
      //
      // Window flag + emit: the flag covers consumers that mount AFTER init
      // resolves (so they can sync-check on mount), the emit covers consumers
      // already mounted (TwinRoute on first page-load).
      (window as { __ejAppReady?: boolean }).__ejAppReady = true;
      emitter.emit("app-ready");
    }
  });
});

//----------------------------------------------------------------

emitter.on("initialized", () => {
  emitter.emit("load");
});

if (isLiveSite()) {
  Sentry.init({
    dsn: "https://636b86f6023b3427350a33986587063b@o1388406.ingest.us.sentry.io/4512101491081216",
    maxBreadcrumbs: 20,
    integrations: [
      Sentry.breadcrumbsIntegration({
        console: false,
        dom: false,
      }),
    ],
    beforeBreadcrumb(breadcrumb) {
      try {
        if (JSON.stringify(breadcrumb).length > 5000) return null;
      } catch {
        return null;
      }
      return breadcrumb;
    },
  });
}